import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";

// Iter 153.70 (Feb 2026) — Photograph treated as ATMOSPHERE, not subject.
// Heavy blur + warm color-grade turns the brass-vase photo into ambient
// background mood; subject focus is the form card and the brand quote.
// Real motion is supplied by the floating-particle field below (CSS-only).
const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/1e05868e-4a04-406e-ac76-fb6799d522f7/images/7c1aadc0a1ffcaadcdd1ddca91bc2adae7bfd104d927c9519e7cadd0ea432aaa.png";

// Floating particle field — generated once at module load, kept stable
// across renders. Each particle has its own size/delay/duration/x-drift
// so the field doesn't look mechanically tiled.
const PARTICLES = Array.from({ length: 22 }, (_, i) => {
  // Pseudo-random but stable so React reconciles identically.
  const seed = (i * 9301 + 49297) % 233280;
  const rand = (n) => ((seed + n * 137) % 1000) / 1000;
  return {
    id: i,
    left: rand(1) * 100,         // 0-100% across viewport
    size: 2 + rand(2) * 4,       // 2-6px
    delay: rand(3) * 14,         // 0-14s stagger
    duration: 14 + rand(4) * 12, // 14-26s total drift time
    xDrift: -30 + rand(5) * 60,  // -30 to +30px horizontal sway
    opacity: 0.35 + rand(6) * 0.4,
  };
});

const LoginPage = () => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success("Welcome back");
    } catch (error) {
      const message =
        error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-stage" data-testid="login-page">
      {/* Atmospheric photograph — heavily blurred warm mood */}
      <div
        className="login-stage-image"
        style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
        aria-hidden="true"
      />
      {/* Warm pulsing light source — gives the canvas living motion */}
      <div className="login-stage-glow" aria-hidden="true" />
      {/* Floating particles — gold dust catching the warm light */}
      <div className="login-stage-particles" aria-hidden="true">
        {PARTICLES.map((p) => (
          <span
            key={p.id}
            className="login-particle"
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              opacity: p.opacity,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              "--x-drift": `${p.xDrift}px`,
            }}
          />
        ))}
      </div>
      {/* Reading wash + film grain on top */}
      <div className="login-stage-overlay" aria-hidden="true" />
      <div className="login-stage-grain" aria-hidden="true" />

      {/* Brand chip — top-left */}
      <header className="login-stage-header login-anim-fade-down">
        <div className="h-12 w-12 rounded-lg bg-white/12 backdrop-blur-md ring-1 ring-white/25 p-1.5">
          <img
            src="/logo512.png"
            alt=""
            className="h-full w-full object-contain"
            data-testid="login-brand-logo"
          />
        </div>
        <div>
          <div
            className="text-xl text-white tracking-tight leading-none"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Exotic
            <span className="font-medium text-[var(--accent-soft)]">India</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/55 mt-1.5">
            Customer Support CRM
          </div>
        </div>
      </header>

      {/* Editorial quote — bottom-left, hidden on small screens */}
      <aside className="login-stage-quote login-anim-fade-up">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--accent-soft)] mb-4">
          Est. 1998 — New Delhi
        </div>
        <h1
          className="text-white leading-[1.08] tracking-tight"
          style={{ fontFamily: "Playfair Display, serif" }}
        >
          <span className="block text-4xl xl:text-5xl font-light">
            Curating the heritage of India,
          </span>
          <span className="block text-4xl xl:text-5xl italic font-light text-white/85 mt-1">
            one conversation at a time.
          </span>
        </h1>
        <div className="mt-6 flex items-center gap-3 text-white/55 text-sm">
          <span className="h-px w-10 bg-[var(--accent-soft)]/70" />
          <span className="tracking-wider">
            Brass • Wood • Stone • Textile • Painting
          </span>
        </div>
      </aside>

      {/* Floating glass-cream form card */}
      <section className="login-card-wrap login-anim-fade-up">
        <div className="login-card" data-testid="login-form-card">
          <div className="mb-7">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--accent)] mb-2">
              Sign in
            </div>
            <h2
              className="text-3xl text-[var(--text-primary)] tracking-tight leading-tight"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Welcome back.
            </h2>
            <p className="mt-2 text-[var(--text-secondary)] text-[14px] leading-relaxed">
              Continue serving customers across WhatsApp, email and voice.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="login-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                className="login-input"
                placeholder="you@exoticindia.com"
                required
                autoComplete="username"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="login-label" htmlFor="login-password">Password</label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                  className="login-input pr-11"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors p-1"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="login-btn-primary w-full mt-1"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Signing in…</span>
                </>
              ) : (
                <span>Sign in</span>
              )}
            </button>
          </form>

          <div className="mt-7 pt-5 border-t border-[var(--border-dark)] text-center text-xs text-[var(--text-secondary)] tracking-wide">
            Accounts are issued by your administrator.
            <br />
            Need access? Email{" "}
            <a
              href="mailto:it@exoticindia.com"
              className="text-[var(--accent)] hover:text-[var(--accent-light)] underline-offset-4 hover:underline"
              data-testid="login-admin-contact"
            >
              it@exoticindia.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
